import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  createCommunityReport,
  fetchCommunityReports,
  fetchCommunityVoteFeed,
  formatCommunityReportApiError,
  voteCommunityReport,
} from "../../../api/communityReports";

const TABS = ["feed", "mine", "report"];
const COMMUNITY_ACCEPTED_STATUSES = new Set(["approved", "in_progress"]);

function statusLabel(t, status) {
  return t(`community.status.${status}`, { defaultValue: status });
}

function partitionCommunityFeedRows(rows) {
  const accepted = [];
  const open = [];

  rows.forEach((row) => {
    if (COMMUNITY_ACCEPTED_STATUSES.has(row.status)) {
      accepted.push(row);
    } else {
      open.push(row);
    }
  });

  return { accepted, open };
}

function statusBadgeClass(status) {
  switch (status) {
    case "approved":
      return "bg-emerald-50 text-emerald-800";
    case "in_progress":
      return "bg-sky-50 text-sky-800";
    case "pending_approval":
      return "bg-amber-50 text-amber-800";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

export default function CommunityPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("feed");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ kind: "bug", title: "", description: "" });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "feed") {
        const res = await fetchCommunityVoteFeed({ perPage: 50 });
        setRows(res?.data || []);
      } else if (tab === "mine") {
        const res = await fetchCommunityReports({ mine: 1, per_page: 50 });
        setRows(res?.data || []);
      }
    } catch {
      toast.error("Could not load community reports");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (tab !== "report") load();
  }, [tab, load]);

  useEffect(() => {
    document.title = `${t("community.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    return () => {
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview);
      }
    };
  }, [screenshotPreview]);

  const clearScreenshot = () => {
    setScreenshotFile(null);
    setScreenshotPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const onScreenshotChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("community.form.image_invalid"));
      return;
    }

    setScreenshotPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setScreenshotFile(file);
  };

  const onVote = async (id) => {
    try {
      const res = await voteCommunityReport(id);
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, ...res.report, voted: res.voted } : row))
      );
    } catch {
      toast.error("Vote failed");
    }
  };

  const renderReportRow = (row) => (
    <div key={row.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t(`community.kind.${row.kind}`, { defaultValue: row.kind })}
          </div>
          <h2 className="mt-1 font-semibold text-slate-900">{row.title}</h2>
          <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{row.description}</p>
          {row.image_url ? (
            <a
              href={row.image_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block"
            >
              <img
                src={row.image_url}
                alt={t("community.form.screenshot_preview")}
                className="max-h-40 rounded-lg border border-slate-200 object-contain"
              />
            </a>
          ) : null}
          {row.triage_summary ? (
            <p className="mt-2 text-xs text-slate-500">
              {t("community.triage_summary")}: {row.triage_summary}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
          >
            {statusLabel(t, row.status)}
          </span>
          {row.votable ? (
            <button
              type="button"
              onClick={() => onVote(row.id)}
              className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm ${
                row.voted ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <HandThumbUpIcon className="h-4 w-4" />
              {row.votes_count} {t("community.votes")}
            </button>
          ) : row.votes_count > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-slate-600">
              <HandThumbUpIcon className="h-4 w-4" />
              {row.votes_count} {t("community.votes")}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderFeedSections = () => {
    const { accepted, open } = partitionCommunityFeedRows(rows);

    return (
      <div className="space-y-8">
        {accepted.length > 0 ? (
          <section aria-labelledby="community-feed-accepted">
            <div className="mb-3">
              <h2 id="community-feed-accepted" className="text-sm font-semibold text-slate-900">
                {t("community.feed.section.accepted")}
              </h2>
              <p className="mt-1 text-xs text-slate-500">{t("community.feed.section.accepted_help")}</p>
            </div>
            <div className="space-y-3">{accepted.map(renderReportRow)}</div>
          </section>
        ) : null}
        {open.length > 0 ? (
          <section aria-labelledby="community-feed-open">
            {accepted.length > 0 ? (
              <h2 id="community-feed-open" className="mb-3 text-sm font-semibold text-slate-900">
                {t("community.feed.section.open")}
              </h2>
            ) : null}
            <div className="space-y-3">{open.map(renderReportRow)}</div>
          </section>
        ) : null}
      </div>
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        kind: form.kind,
        title: form.title.trim(),
        description: form.description.trim(),
      };
      await createCommunityReport(payload, screenshotFile);
      toast.success(t("community.form.success"));
      setForm({ kind: "bug", title: "", description: "" });
      clearScreenshot();
      setTab("mine");
    } catch (err) {
      toast.error(
        formatCommunityReportApiError(
          err?.response?.data,
          t("community.form.error", { defaultValue: "Could not submit report" })
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-800">
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            Promang
          </div>
          <h1 className="text-2xl font-semibold text-slate-950">{t("community.title")}</h1>
          <p className="mt-1 text-sm text-slate-600">{t("community.subtitle")}</p>
        </div>

        <div className="mb-4 flex gap-2">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                tab === key ? "bg-sky-700 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"
              }`}
            >
              {t(`community.tab.${key}`)}
            </button>
          ))}
        </div>

        {tab === "report" ? (
          <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <label className="block text-sm font-medium text-slate-700">
              {t("community.form.kind")}
              <select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="bug">{t("community.kind.bug")}</option>
                <option value="feature">{t("community.kind.feature")}</option>
                <option value="improvement">{t("community.kind.improvement")}</option>
              </select>
            </label>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              {t("community.form.title")}
              <input
                required
                maxLength={200}
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              {t("community.form.description")}
              <textarea
                required
                minLength={10}
                rows={6}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <div className="mt-4">
              <span className="block text-sm font-medium text-slate-700">
                {t("community.form.screenshot")}
              </span>
              <p className="mt-1 text-xs text-slate-500">{t("community.form.screenshot_help")}</p>
              {screenshotPreview ? (
                <div className="relative mt-3 inline-block">
                  <img
                    src={screenshotPreview}
                    alt={t("community.form.screenshot_preview")}
                    className="max-h-48 rounded-lg border border-slate-200 object-contain"
                  />
                  <button
                    type="button"
                    onClick={clearScreenshot}
                    className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-slate-500 shadow ring-1 ring-slate-200 hover:text-slate-800"
                    aria-label={t("community.form.screenshot_remove")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 hover:border-sky-300 hover:bg-sky-50/50">
                  <PhotoIcon className="h-5 w-5 shrink-0 text-slate-400" />
                  <span>{t("community.form.screenshot_add")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onScreenshotChange}
                    className="sr-only"
                  />
                </label>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 rounded-lg bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800 disabled:opacity-50"
            >
              {submitting ? t("community.form.submitting") : t("community.form.submit")}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            {loading ? (
              <div className="rounded-xl bg-white p-6 text-sm text-slate-500 shadow-sm">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="rounded-xl bg-white p-6 text-sm text-slate-500 shadow-sm">
                {tab === "mine" ? t("community.empty_mine") : t("community.empty_feed")}
              </div>
            ) : tab === "feed" ? (
              renderFeedSections()
            ) : (
              rows.map((row) => renderReportRow(row))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
